import cn from 'classnames';
import { capitalize } from 'lodash';
import _ from 'lodash/fp';
import filter from 'lodash/fp/filter';
import get from 'lodash/fp/get';
import orderBy from 'lodash/fp/orderBy';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useDispatch, useSelector } from 'react-redux';

import useEventListener from '../../hooks/useEventListener';
import { getAdminJobsList, getJobsList } from '../../redux/actions';
import { profileSelectors } from '../../redux/selectors/profile';
import text from '../../text';
import { isStaff } from '../../utilities/user';
import Loading from '../loading/Loading';
import { STATUS, UNTITLED, statuses } from './consts';
import StatusFilterIcon from './filters/StatusFilterIcon';
import InviteRow from './inviteRow';
import OrderListingItem, { COLUMN } from './orderListingItem';
import EmptyState from '../common/EmptyState';
import { JobListing, User } from '../../redux/rtk';

const NUM_SHOW_INITIAL = 20;
const NUM_SHOW_MORE = 10;

const isAscendingOrder = (order: string) => order && order === 'asc';

const Row = ({
  data: { item, currentUser, isAdminOrigin, isStaff },
  index,
  style,
}: {
  data: {
    item: { type: string; job: JobListing };
    currentUser: User;
    isAdminOrigin: boolean;
    isStaff: boolean;
  };
  index?: number;
  style?: React.CSSProperties;
}) => {
  // const item = useMemo(() => listItems[index], [listItems]);
  return item.type === 'invite' ? (
    <InviteRow key={index} job={item.job} style={style} />
  ) : (
    <OrderListingItem
      key={index}
      job={item.job}
      currentUser={currentUser}
      isAdminOrigin={isAdminOrigin}
      isStaff={isStaff}
      style={style || {}}
    />
  );
};

interface ProjectListingProps {
  jobsList: JobListing[];
  currentUser: User;
  statusCounts: Record<string, number>;
  isAdminOrigin?: boolean;
  isStaff: boolean;
  isLoading: boolean;
  status: { status: string[] };
  searchText: string;
}

export const ProjectListing = ({
  jobsList,
  currentUser,
  statusCounts,
  isAdminOrigin = false,
  isStaff,
  isLoading,
  status,
  searchText = '',
}: ProjectListingProps) => {
  const [sortingProperty, setSortingProperty] = useState('updated_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [orderedJobsList, setOrderedJobsList] = useState<JobListing[]>([]);
  const filterStatus = status.status;
  const [showUpTo, setShowUpTo] = useState(NUM_SHOW_INITIAL);
  const [orderListingElement, setOrderListingElement] =
    useState<HTMLDivElement | null>(null);
  const [height, setHeight] = useState(0);

  const refCallback = useCallback((node: HTMLDivElement | null) => {
    setOrderListingElement(node);
  }, []);

  useEffect(() => {
    if (jobsList) {
      const matchedList = Object.values(jobsList).reduce<JobListing[]>(
        (acc, job) => {
          const matchingJobs =
            job.jobs?.filter((j) => filterStatus.includes(j.status)) ?? [];
          if (matchingJobs.length === 0) return acc;
          acc.push({
            ...job,
            ...matchingJobs[0],
            project: job.project,
            jobs: matchingJobs,
            created_at: job.created_at,
            updated_at: job.updated_at,
            updater: job.updater,
          });
          return acc;
        },
        []
      );

      const sortedList = orderBy(
        sortingProperty === 'status'
          ? [(job: JobListing) => statuses[job.status].order]
          : [get(sortingProperty)],
        [sortOrder],
        matchedList
      );

      const filteredList = filter(({ project, full_address }) => {
        const filterBySearchText = (
          projectName: string,
          fullAddress: string
        ) => {
          const searchByText = (property: string) => {
            return property.toLowerCase().includes(searchText.toLowerCase());
          };

          if (searchText === '') return true;
          if (projectName)
            return searchByText(projectName) || searchByText(fullAddress);
          if (projectName === null)
            return searchByText(UNTITLED) || searchByText(fullAddress);
          return false;
        };

        return filterBySearchText(project?.name, full_address);
      })(sortedList);

      setOrderedJobsList(filteredList as unknown as JobListing[]);
    }
  }, [sortingProperty, sortOrder, jobsList, status, filterStatus, searchText]);

  const handleSetSortingProperty = (property: string) => {
    setSortingProperty(property);
    setSortOrder(isAscendingOrder(sortOrder) ? 'desc' : 'asc');
  };

  const renderSortableHeading = (
    header: string,
    propertyId: string,
    className: string
  ) => {
    return (
      <th
        className={cn('column-header', {
          [className]: className,
        })}
        onClick={() => handleSetSortingProperty(propertyId)}
      >
        {propertyId === 'status' ? (
          <>
            <StatusFilterIcon status={filterStatus} />
            <span className='mx-4'>{header}</span>
          </>
        ) : (
          header
        )}

        <a className='up-icon'>
          {sortingProperty === propertyId ? (
            <img
              src={
                !isAscendingOrder(sortOrder)
                  ? '/public/img/icon-up.svg'
                  : '/public/img/icon-down.svg'
              }
            />
          ) : (
            <div />
          )}
        </a>
      </th>
    );
  };

  const noOrdersAvailable = [...filterStatus].map((status) => {
    if (status === 'pending') return 'Quoted';
    if (status === 'inprogress') return 'Commenced';
    return capitalize(status);
  });

  const totalNumListItems = orderedJobsList?.length;
  const { listItems, numListItems } = useMemo(() => {
    if (!orderedJobsList) {
      return { listItems: [], numListItems: 0 };
    }

    return orderedJobsList.reduce<{
      listItems: { type: string; job: JobListing }[];
      numListItems: number;
    }>(
      ({ listItems, numListItems }, job: JobListing) => {
        if (numListItems > showUpTo) {
          return { listItems, numListItems };
        }

        // temporarily exclude draft jobs from sorting and avoid showing Draft as min-status
        // sort jobs by status.order to show min-status - max-status in dashboard/orderListingItem.tsx
        const jobs = job.jobs;

        const nonDraftJobs =
          jobs.length > 1
            ? jobs.filter((j) => j.status !== STATUS.DRAFT)
            : jobs;

        const jobsToSort = nonDraftJobs.length > 0 ? nonDraftJobs : jobs;
        const sortedJobs = jobsToSort
          .map((j) => ({ ...j, statusInfo: statuses[j.status] }))
          .sort((a, b) => a.statusInfo.order - b.statusInfo.order);

        listItems.push({
          type: 'job',
          job: { ...job, jobs: sortedJobs },
        });

        if (job.user_project?.invite_status === 'pending') {
          listItems.push({ type: 'invite', job });
        }
        numListItems++;
        return { listItems, numListItems };
      },
      { listItems: [], numListItems: 0 }
    );
  }, [orderedJobsList, showUpTo]);

  const showMore = () => {
    setShowUpTo((prev) => prev + NUM_SHOW_MORE);
  };

  const getOrderListingContainerHeight = useCallback(() => {
    if (orderListingElement && listItems.length > 0) {
      setTimeout(() => {
        const height = orderListingElement.clientHeight;
        if (height) {
          setHeight(height);
        }
      }, 300);
    }
  }, [orderListingElement, listItems.length]);

  useEventListener('resize', getOrderListingContainerHeight);

  useLayoutEffect(() => {
    if (orderListingElement && listItems.length > 0) {
      getOrderListingContainerHeight();
    }
  }, [orderListingElement, listItems.length, getOrderListingContainerHeight]);

  if (orderedJobsList === undefined) {
    return null;
  }

  const availableStatuses = Object.keys(statusCounts).filter(
    (status) => statusCounts[status] > 0
  );

  const hasExistingProject =
    availableStatuses.length > 1 ||
    (availableStatuses.length === 1 && availableStatuses[0] !== STATUS.DEMO);

  return !isLoading ? (
    <div
      id='OrderListing'
      className={cn('position-relative d-flex flex-column flex-fill', {
        'overflow-auto': !hasExistingProject,
        'overflow-hidden': hasExistingProject,
      })}
      ref={refCallback}
    >
      {listItems.length === 0 && (
        <div className='p-4 no-order-container'>
          <p className='text-center mb-0'>
            {text('noOrdersAvailable', {
              status: (
                <span className='font-weight-bold'>
                  {noOrdersAvailable.join(', ')}.
                </span>
              ),
            })}
          </p>
        </div>
      )}

      {listItems.length > 0 && (
        <InfiniteScroll
          dataLength={listItems.length}
          next={showMore}
          hasMore={numListItems < totalNumListItems}
          loader={<Loading />}
          scrollableTarget='order-table'
        >
          <div
            className='dashboard-table table-responsive'
            id='order-table'
            style={{
              ...(height && {
                maxHeight: `${height}px`,
              }),
            }}
          >
            <table className='table'>
              <thead>
                <tr>
                  {renderSortableHeading(
                    text('projectName'),
                    'project.name',
                    COLUMN.NAME
                  )}
                  {renderSortableHeading(
                    text('siteAddress'),
                    'full_address',
                    COLUMN.ADDRESS
                  )}
                  {/* Temporarily hide order count column: https://larki.atlassian.net/browse/LP-3543 */}
                  {/* <th className={COLUMN.ORDER_COUNT} /> */}
                  {renderSortableHeading(
                    text('createdDate'),
                    'created_at',
                    COLUMN.DATE
                  )}
                  {renderSortableHeading(
                    text('updatedDate'),
                    'updated_at',
                    COLUMN.DATE
                  )}
                  {renderSortableHeading(
                    text('status'),
                    'status',
                    COLUMN.STATUS
                  )}
                  <th className={COLUMN.ACTIONS}>{text('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {_.map(
                  (item) => (
                    <Row
                      key={item.job.id}
                      data={{
                        item,
                        currentUser,
                        isAdminOrigin,
                        isStaff,
                      }}
                    />
                  ),
                  listItems
                )}
              </tbody>
            </table>
          </div>
        </InfiniteScroll>
      )}

      {!hasExistingProject && (
        <div className='d-flex justify-content-center'>
          <EmptyState className='mt-5' />
        </div>
      )}
    </div>
  ) : (
    <Loading />
  );
};

export const useProjectListing = () => {
  const isAdmin = useSelector(profileSelectors.getIsAdmin);
  const { currentUser, status, searchText, ...state } = useSelector(
    (state: {
      jobsReducer: {
        jobsList: JobListing[];
        statusCounts: Record<string, number>;
        isJobsListLoading: boolean;
        adminJobList: JobListing[];
        adminStatusCounts: Record<string, number>;
        isAdminJobListLoading: boolean;
        listJobFilter: {
          status: string[];
          searchText: string;
        };
      };
      profileReducer: {
        userProfile: User;
      };
    }) => ({
      jobsList: state.jobsReducer.jobsList,
      statusCounts: state.jobsReducer.statusCounts,
      isJobsListLoading: state.jobsReducer.isJobsListLoading,

      adminJobList: state.jobsReducer.adminJobList,
      adminStatusCounts: state.jobsReducer.adminStatusCounts,
      isAdminJobListLoading: state.jobsReducer.isAdminJobListLoading,

      currentUser: state.profileReducer.userProfile,
      status: state.jobsReducer.listJobFilter,
      searchText: state.jobsReducer.listJobFilter.searchText,
    })
  );
  const listingParams = useMemo(
    () =>
      isAdmin
        ? {
            isLoading: state.isAdminJobListLoading,
            jobsList: state.adminJobList,
            statusCounts: state.adminStatusCounts,
          }
        : {
            isLoading: state.isJobsListLoading,
            jobsList: state.jobsList,
            statusCounts: state.statusCounts,
          },
    [isAdmin, state]
  );
  return { isAdmin, currentUser, status, searchText, ...listingParams };
};

const ProjectListingWithRedux = () => {
  const dispatch = useDispatch();

  const {
    isAdmin,
    currentUser,
    status,
    searchText,
    isLoading,
    jobsList,
    statusCounts,
  } = useProjectListing();

  // todo: only fetch once
  useEffect(() => {
    if (isAdmin) {
      dispatch(getAdminJobsList());
    } else {
      dispatch(getJobsList());
    }
  }, [dispatch, isAdmin]);

  return (
    <ProjectListing
      currentUser={currentUser}
      isLoading={isLoading}
      isStaff={isStaff(currentUser?.role)}
      jobsList={jobsList}
      statusCounts={statusCounts}
      status={status}
      searchText={searchText}
    />
  );
};

export default ProjectListingWithRedux;
